import { computed, inject, Injectable } from "@angular/core";
import { injectQuery, injectMutation, queryOptions } from "@ngneat/query";
import { derivedAsync } from "ngxtension/derived-async";
import { toSignal } from "@angular/core/rxjs-interop";
import { TenantUrlUtilService } from "@shared/services/tenant-url-util.service";
import { TipificationService } from "./services/tipification.service";
import {
  TipificationReason,
  TipificationPayload,
  TipificationFormValue,
} from "./models/tipification.model";
import { SelectOption } from "@shared/components/ui/select/select.component";
import { TIPIFICATION_DEFAULTS } from "./constants/tipification.constants";
import { StoreService } from "@shared/services/store.service";

@Injectable({
  providedIn: "root",
})
export class TipificationState {
  private readonly tipificationService = inject(TipificationService);
  private readonly query = injectQuery();
  private readonly mutation = injectMutation();
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly storeService = inject(StoreService);

  private readonly queueId = this.tenantUrlUtil.queueId;

  private getReasonsQueryOptions(queueId: string) {
    return queryOptions({
      queryKey: ["tipification", "reasons", queueId] as const,
      queryFn: async () => {
        const data = await this.tipificationService.fetchReasons(queueId);
        return data;
      },
      enabled: !!queueId,
      retry: 5,
    });
  }

  readonly reasonsQueryAsync = derivedAsync(() => {
    const queue = this.queueId();
    if (!queue) {
      return null;
    }
    return this.query(this.getReasonsQueryOptions(queue)).result$;
  });

  readonly isLoading = computed(() => {
    const query = this.reasonsQueryAsync();
    if (!query) return false;
    return query.isLoading;
  });

  readonly hasError = computed(() => {
    const query = this.reasonsQueryAsync();
    if (!query) return false;
    return query.isError;
  });

  readonly reasons = computed<TipificationReason[]>(() => {
    const query = this.reasonsQueryAsync();
    if (!query) return [];
    return query.data ?? [];
  });

  readonly reasonOptions = computed<SelectOption[]>(() => {
    const reasonsList = this.reasons();
    return reasonsList.map((reason) => ({
      value: reason.id,
      label: reason.name,
    }));
  });

  readonly hasReasons = computed(() => {
    return this.reasons().length > 0;
  });

  readonly createTipificationMutation = this.mutation({
    mutationFn: async (payload: TipificationPayload) => {
      return await this.tipificationService.createTipification(payload);
    },
  });

  private readonly mutationResult = toSignal(
    this.createTipificationMutation.result$,
  );

  readonly isSuccess = computed(() => {
    return this.mutationResult()?.isSuccess ?? false;
  });

  readonly isError = computed(() => {
    return this.mutationResult()?.isError ?? false;
  });

  buildPayload(
    formValue: TipificationFormValue,
    identityDocument: string | null,
  ): TipificationPayload {
    const conversationId = this.tenantUrlUtil.conversationId();
    const participantIdCustomer = this.tenantUrlUtil.participantIdCustomer();
    const participantIdAgent = this.tenantUrlUtil.participantIdAgent();
    const typeInteraction = this.tenantUrlUtil.typeInteraction();

    const selectedReason = this.reasons().find(
      (r) => r.id === formValue.motivo,
    );

    const finalConversationId = conversationId || "";
    const finalTienda = formValue.tienda || "";
    
    // Get store name for subtienda (send name instead of code)
    let finalSubtienda = formValue.subtienda || "";
    if (finalSubtienda && finalSubtienda !== "Venta Online") {
      const store = this.storeService.getStoreByCachedCode(finalSubtienda);
      finalSubtienda = store?.nombreTienda || finalSubtienda;
    }
    
    const finalVendedor = formValue.vendedor || "";
    const finalDocumento = identityDocument || "";

    return {
      conversationId: finalConversationId,
      participantIdCustomer: participantIdCustomer || "",
      participantIdAgent: participantIdAgent || "",
      typeInteraction:
        typeInteraction || TIPIFICATION_DEFAULTS.DEFAULT_TYPE_INTERACTION,
      attributes: {
        tienda: finalTienda,
        subtienda: finalSubtienda,
        incidencia_generada: formValue.incidenciaGenerada ? "SI" : "NO",
        solucionada_llamada: formValue.solucionadoEnInteraccion ? "SI" : "NO",
        vendedor: finalVendedor,
      },
      wrapup: {
        code: formValue.motivo || "",
        name: selectedReason?.name || "",
        notes: formValue.observaciones || "ninguna",
        provisional: TIPIFICATION_DEFAULTS.PROVISIONAL,
      },
      participantAttributes: {
        org: TIPIFICATION_DEFAULTS.ORG_ID,
        convId: finalConversationId,
        atributos: [
          {
            tipoId: TIPIFICATION_DEFAULTS.ATTRIBUTE_TYPE_TIENDA,
            valor: finalTienda,
          },
          {
            tipoId: TIPIFICATION_DEFAULTS.ATTRIBUTE_TYPE_SUBTIENDA,
            valor: finalSubtienda,
          },
          {
            tipoId: TIPIFICATION_DEFAULTS.ATTRIBUTE_TYPE_SOLUCIONADA,
            valor: formValue.solucionadoEnInteraccion ? "SI" : "NO",
          },
          {
            tipoId: TIPIFICATION_DEFAULTS.ATTRIBUTE_TYPE_INCIDENCIA,
            valor: formValue.incidenciaGenerada ? "SI" : "NO",
          },
          {
            tipoId: TIPIFICATION_DEFAULTS.ATTRIBUTE_TYPE_DOCUMENTO,
            valor: finalDocumento,
          },
          {
            tipoId: TIPIFICATION_DEFAULTS.SELLER_TYPE,
            valor: finalVendedor,
          },
          {
            tipoId: TIPIFICATION_DEFAULTS.ATTRIBUTE_TYPE_TIENDA,
            valor: finalTienda,
          },
        ],
      },
    };
  }

  createTipification(payload: TipificationPayload) {
    return this.createTipificationMutation.mutate(payload);
  }

  resetSubmitState() {
    this.createTipificationMutation.reset();
  }
}
